import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { subscribeToEmotes, addEmote, deleteEmote, reorderEmotes, createTheme, deleteTheme, updateThemeOverride, subscribeToThemes, setActiveTheme, subscribeToSystemConfig, checkThemeStorageExists, createThemeFolder, restoreThemeFromStorage, checkFileExists, uploadEmoteImage, removeThemeOverride, getFileUrl } from '../services/firestore.js';
import { Trash2, Plus, Upload, Image as ImageIcon, ChevronDown, ChevronUp, ToggleLeft, ToggleRight, Check, Sparkles } from 'lucide-react';

export default function AdminEmoteManager({ onBack }) {
    const [emotes, setEmotes] = useState([]);
    const [themes, setThemes] = useState([]);
    const [activeThemeId, setActiveThemeId] = useState(null); // null = Default
    const [loading, setLoading] = useState(true);
    const [draggedEmote, setDraggedEmote] = useState(null);
    const [expandedTheme, setExpandedTheme] = useState(null); // 'default' or themeId

    // Form states
    const [newEmote, setNewEmote] = useState({ code: '', name: '', url: '', tier: 'global' });
    const [newThemeName, setNewThemeName] = useState('');
    const [showAddTheme, setShowAddTheme] = useState(false);

    useEffect(() => {
        // Subscribe to everything
        const unsubEmotes = subscribeToEmotes(setEmotes);
        const unsubThemes = subscribeToThemes(setThemes);
        const unsubConfig = subscribeToSystemConfig((config) => {
            setActiveThemeId(config.activeThemeId || null);
        });

        setLoading(false);

        return () => {
            unsubEmotes();
            unsubThemes();
            unsubConfig();
        };
    }, []);

    // ... Existing Handlers (handleAddEmote, handleDelete, onDrop etc) ...
    // Note: Emote Deletion needs to be careful if themes reference it, but overrides are just keys, so they'll just be orphans. fine.

    const handleAddEmote = async (e) => {
        e.preventDefault();
        try {
            if (!newEmote.code || !newEmote.url) { alert("Code and URL required"); return; }
            let code = newEmote.code.trim();
            if (!code.startsWith(':')) code = ':' + code;
            if (!code.endsWith(':')) code = code + ':';

            const existing = emotes.find(e => e.code === code);
            if (existing) { alert("Code exists!"); return; }

            // Check limit
            const tierCount = emotes.filter(e => e.tier === newEmote.tier).length;
            if (tierCount >= 10) { alert(`Tier full! Delete one first.`); return; }

            await addEmote(code, newEmote.name || code, newEmote.url, newEmote.tier);
            setNewEmote(prev => ({ code: '', name: '', url: '', tier: prev.tier }));
            alert("Emote added!");
        } catch (err) { console.error(err); alert("Failed"); }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Delete emote?")) await deleteEmote(id);
    };

    // Theme Handlers
    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // 1. Validation
        if (file.type !== 'image/png') { alert("Only PNG files allowed"); return; }
        if (!newEmote.code) { alert("Please enter a Code first to name the file."); return; }

        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = async () => {
            // 2. Client-Side Resizing (Target: 256x256 for QD quality)
            const canvas = document.createElement('canvas');
            canvas.width = 256;
            canvas.height = 256;
            const ctx = canvas.getContext('2d');

            // Draw and resize
            ctx.drawImage(img, 0, 0, 256, 256);

            // Export to Blob
            canvas.toBlob(async (blob) => {
                if (!blob) { alert("Resize failed"); return; }

                // 2. Naming & Conflict Check
                const cleanCode = newEmote.code.replace(/:/g, '');
                const filename = `${cleanCode}.png`;
                const folder = 'emotes/images/default';
                const path = `${folder}/${filename}`;

                try {
                    const exists = await checkFileExists(path);
                    if (exists) {
                        alert(`Error: A file named "${filename}" already exists in default storage.`);
                        return;
                    }

                    // 3. Upload Blob
                    setLoading(true);
                    // uploadEmoteImage expects a File or Blob
                    const url = await uploadEmoteImage(blob, folder, filename);
                    setNewEmote(prev => ({ ...prev, url }));
                    alert("Upload and resize successful! URL set.");
                } catch (err) {
                    console.error(err);
                    alert("Upload failed.");
                } finally {
                    setLoading(false);
                }
            }, 'image/png');
        };
    };

    const handleCreateTheme = async (e) => {
        e.preventDefault();
        const cleanName = newThemeName.trim();
        if (!cleanName) {
            alert("Theme name cannot be empty.");
            return;
        }

        try {
            // Check storage first
            const exists = await checkThemeStorageExists(cleanName);

            if (exists) {
                // If folder exists, ask to RESTORE or ABORT (don't create duplicate folder)
                const shouldRestore = window.confirm(`A storage folder "${cleanName}" already exists. \n\nClick OK to RESTORE overrides from this folder.\nClick Cancel to abort.`);

                if (shouldRestore) {
                    const restoredOverrides = await restoreThemeFromStorage(cleanName, emotes);
                    await createTheme(cleanName, restoredOverrides); // Create with overrides
                    alert(`Theme "${cleanName}" restored with ${Object.keys(restoredOverrides).length} overrides.`);
                } else {
                    return; // Abort
                }
            } else {
                // Fresh Create
                await createThemeFolder(cleanName);
                await createTheme(cleanName);
                alert(`Theme "${cleanName}" created successfully! Check Storage for new folder.`);
            }

            setNewThemeName('');
            setShowAddTheme(false);
        } catch (error) {
            console.error(error);
            alert("Failed to create theme.");
        }
    };

    const handleToggleActive = async (themeId) => {
        // If clicking the current active one, do nothing (user can switch to Default explicitly)
        // Or if unchecking, switch to Default? User said "toggle... on and off". 
        // If I turn OFF the active theme, fallback to Default (null).
        if (activeThemeId === themeId) {
            await setActiveTheme(null);
        } else {
            await setActiveTheme(themeId);
        }
    };

    // Override Modal State
    const [editingOverride, setEditingOverride] = useState(null); // { themeId, themeName, emoteId, emoteCode }
    const [overrideUrl, setOverrideUrl] = useState('');

    const handleOpenOverride = async (themeId, themeName, emoteId, emoteCode) => {
        setEditingOverride({ themeId, themeName, emoteId, emoteCode });
        setOverrideUrl(''); // Reset

        // Auto-check Storage
        const cleanCode = emoteCode.replace(/:/g, '').toLowerCase();
        const path = `emotes/images/${themeName}/${cleanCode}.png`;
        const existingUrl = await getFileUrl(path);

        if (existingUrl) {
            setOverrideUrl(existingUrl);
        }
    };

    const handleSaveOverride = async () => {
        if (!editingOverride || !overrideUrl) return;
        await updateThemeOverride(editingOverride.themeId, editingOverride.emoteId, overrideUrl);
        setEditingOverride(null);
    };

    const handleOverrideFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file || !editingOverride) return;

        if (file.type !== 'image/png') { alert("Only PNG files allowed"); return; }

        // Auto-process/resize could be done here if needed, but `uploadEmoteImage` handles it (or backend triggers)
        // User requested consistency with base upload which just takes the file.

        // Path: emotes/images/{themeName}/{cleanCode}.png
        const cleanCode = editingOverride.emoteCode.replace(/:/g, '').toLowerCase(); // Standardize
        const filename = `${cleanCode}.png`;
        const folder = `emotes/images/${editingOverride.themeName}`;
        const path = `${folder}/${filename}`;

        try {
            const exists = await checkFileExists(path);
            if (exists) {
                if (!confirm(`File "${filename}" exists in this theme folder. Overwrite?`)) return;
            }

            setLoading(true);
            const url = await uploadEmoteImage(file, folder, filename);
            setOverrideUrl(url); // Auto-fill input
            alert("Upload successful!");
        } catch (err) {
            console.error(err);
            alert("Upload failed.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteOverride = async (themeId, emoteId) => {
        if (confirm("Revert this emote to default?")) {
            await removeThemeOverride(themeId, emoteId);
        }
    };

    const RenderOverrideModal = () => {
        if (!editingOverride) return null;

        return createPortal(
            React.createElement('div', {
                style: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }
            },
                React.createElement('div', { className: 'glass-panel', style: { width: '400px', padding: '20px', background: '#18181b', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '15px' } },
                    React.createElement('h3', { style: { margin: 0 } }, `Edit Override: ${editingOverride.emoteCode}`),

                    // URL Input
                    React.createElement('input', {
                        value: overrideUrl,
                        onChange: e => setOverrideUrl(e.target.value),
                        placeholder: "Enter Image URL...",
                        className: 'input-field'
                    }),

                    React.createElement('div', { style: { textAlign: 'center', color: '#666', fontSize: '12px', fontWeight: 'bold' } }, "- OR -"),

                    // Upload Button
                    React.createElement('div', {},
                        React.createElement('input', {
                            type: 'file',
                            accept: 'image/png',
                            id: 'override-upload',
                            style: { display: 'none' },
                            onChange: handleOverrideFileSelect
                        }),
                        React.createElement('button', {
                            onClick: () => document.getElementById('override-upload').click(),
                            className: 'btn-secondary',
                            style: { width: '100%', justifyContent: 'center', borderRadius: '12px' }
                        }, "Upload PNG")
                    ),

                    // Actions
                    React.createElement('div', { style: { display: 'flex', gap: '10px', marginTop: '10px' } },
                        React.createElement('button', { onClick: () => setEditingOverride(null), className: 'btn-ghost', style: { flex: 1, borderRadius: '12px' } }, "Cancel"),
                        React.createElement('button', { onClick: handleSaveOverride, className: 'btn-primary', style: { flex: 1, borderRadius: '12px' } }, "Save")
                    )
                )
            ),
            document.body
        );
    };

    // DnD Handlers (re-use logic for Default Pack)
    const onDragStart = (e, emote) => { setDraggedEmote(emote); e.dataTransfer.effectAllowed = 'move'; };
    const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
    const onDrop = async (e, targetTier, targetIndex) => {
        e.preventDefault();
        if (!draggedEmote) return;
        let targetTierEmotes = emotes.filter(e => e.tier === targetTier && e.id !== draggedEmote.id);
        if (targetIndex === -1) targetTierEmotes.push({ ...draggedEmote, tier: targetTier });
        else targetTierEmotes.splice(targetIndex, 0, { ...draggedEmote, tier: targetTier });

        const updates = [];
        targetTierEmotes.forEach((emote, index) => updates.push({ id: emote.id, tier: targetTier, order: index }));
        await reorderEmotes(updates);
        setDraggedEmote(null);
    };

    // Render Helper: Emote Grid
    // isThemeMode: boolean. IF true, clicking edits Override. IF false, DnD enabled + Edit Base.
    const RenderTierGrid = ({ tier, theme = null }) => {
        const tierEmotes = emotes.filter(e => e.tier === tier);
        const totalSlots = Math.max(10, tierEmotes.length);
        const placeholdersNeeded = Math.max(0, totalSlots - tierEmotes.length);

        const displayItems = [
            ...tierEmotes,
            ...Array(placeholdersNeeded).fill({ isPlaceholder: true })
        ].map((item, idx) => item.isPlaceholder ? { ...item, id: `placeholder-${tier}-${idx}` } : item);

        return React.createElement('div', {
            style: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }
        },
            displayItems.map((item, index) => {
                if (item.isPlaceholder) {
                    return React.createElement('div', {
                        key: item.id,
                        onDragOver: (e) => !theme && onDragOver(e), // Only allow drop in Default Mode
                        onDrop: (e) => {
                            if (!theme) { e.stopPropagation(); onDrop(e, tier, index); }
                        },
                        style: {
                            height: '140px', background: 'rgba(255,255,255,0.02)',
                            border: '1px dashed rgba(255,255,255,0.05)', borderRadius: '8px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5
                        }
                    }, React.createElement(ImageIcon, { size: 24 }));
                }

                // Determine Display URL
                const isOverridden = theme && theme.overrides && theme.overrides[item.id];
                const displayUrl = isOverridden ? theme.overrides[item.id] : item.url;

                return React.createElement('div', {
                    key: item.id,
                    className: 'bounce-hover',
                    draggable: !theme, // Only draggable in Default
                    onDragStart: (e) => !theme && onDragStart(e, item),
                    onDragOver: (e) => !theme && onDragOver(e),
                    onDrop: (e) => { if (!theme) { e.stopPropagation(); onDrop(e, tier, index); } },
                    onClick: () => {
                        if (theme) {
                            // Theme Mode: Open Modal
                            handleOpenOverride(theme.id, theme.name, item.id, item.code);
                        } else {
                            // Default Mode: Edit Base
                            setNewEmote({ code: item.code, name: item.name, url: item.url, tier: item.tier });
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                    },
                    style: {
                        background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '10px',
                        position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', height: '140px', gap: '8px', cursor: 'pointer',
                        border: isOverridden ? '1px solid #ecc94b' : '1px solid rgba(255,255,255,0.05)' // Highlight overrides
                    }
                },
                    React.createElement('div', { style: { fontSize: '11px', fontWeight: 'bold' } }, item.name || 'Unnamed'),
                    React.createElement('img', {
                        src: displayUrl,
                        style: { height: '40px', objectFit: 'contain', opacity: (!isOverridden && theme) ? 0.5 : 1 }
                    }),
                    React.createElement('code', { style: { background: 'rgba(0,0,0,0.3)', padding: '4px', borderRadius: '4px', fontSize: '10px' } }, item.code),
                    (!theme || (theme && isOverridden)) && React.createElement('button', {
                        onClick: (e) => {
                            e.stopPropagation();
                            if (theme) handleDeleteOverride(theme.id, item.id);
                            else handleDelete(item.id);
                        },
                        style: { position: 'absolute', top: 5, right: 5, background: 'rgba(0,0,0,0.5)', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', color: '#ef4444', cursor: 'pointer' }
                    }, React.createElement(Trash2, { size: 12 }))
                );
            })
        );
    }

    return React.createElement('div', { className: 'animate-fade-in', style: { maxWidth: '1200px', margin: '0 auto', paddingBottom: '100px' } },
        // HEADER
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px' } },
            React.createElement('button', { onClick: onBack, className: 'btn-ghost' }, '← Back'),
            React.createElement('h1', { style: { margin: 0 } }, 'Emote Packs')
        ),

        // MAIN LAYOUT: 2 Columns
        React.createElement('div', { style: { display: 'flex', gap: '30px', alignItems: 'flex-start' } },

            // LEFT COLUMN: Emote Lists
            React.createElement('div', { style: { flex: 1 } },
                // DEFAULT PACK (Collapsible)
                React.createElement('div', { className: 'glass-panel', style: { marginBottom: '20px', overflow: 'hidden' } },
                    React.createElement('div', {
                        onClick: () => setExpandedTheme(expandedTheme === 'default' ? null : 'default'),
                        style: { padding: '20px', background: 'rgba(255,255,255,0.03)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }
                    },
                        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
                            React.createElement('h2', { style: { margin: 0, fontSize: '18px' } }, "Default"),
                            activeThemeId === null && React.createElement('span', { style: { fontSize: '12px', background: '#22c55e', color: '#fff', padding: '2px 8px', borderRadius: '12px' } }, "ACTIVE")
                        ),
                        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '15px' } },
                            // Toggle for Default (Activates null)
                            React.createElement('button', {
                                onClick: (e) => { e.stopPropagation(); handleToggleActive(null); },
                                style: { background: 'none', border: 'none', cursor: 'pointer', color: activeThemeId === null ? '#22c55e' : '#666' }
                            }, activeThemeId === null ? React.createElement(ToggleRight, { size: 32 }) : React.createElement(ToggleLeft, { size: 32 })),
                            expandedTheme === 'default' ? React.createElement(ChevronUp, { size: 20 }) : React.createElement(ChevronDown, { size: 20 })
                        )
                    ),
                    expandedTheme === 'default' && React.createElement('div', { style: { padding: '20px' } },
                        // ADD FORM REMOVED FROM HERE
                        // TIERS
                        ['global', 'subscriber', 'super_fan', 'mega_fan'].map(tier =>
                            React.createElement('div', { key: tier, style: { marginBottom: '30px' } },
                                React.createElement('h4', { style: { marginBottom: '10px', textTransform: 'uppercase', opacity: 0.7, fontSize: '12px' } },
                                    tier === 'subscriber' ? 'Supporter' : (tier === 'super_fan' ? 'Super Fan' : (tier === 'mega_fan' ? 'Mega Fan' : 'Global'))
                                ),
                                React.createElement(RenderTierGrid, { tier, theme: null })
                            )
                        )
                    )
                ),

                // THEME PACKS
                themes.map(theme =>
                    React.createElement('div', { key: theme.id, className: 'glass-panel', style: { marginBottom: '15px', overflow: 'hidden', border: activeThemeId === theme.id ? '1px solid #22c55e' : 'none' } },
                        React.createElement('div', {
                            onClick: () => setExpandedTheme(expandedTheme === theme.id ? null : theme.id),
                            style: { padding: '20px', background: 'rgba(255,255,255,0.03)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }
                        },
                            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
                                React.createElement('h2', { style: { margin: 0, fontSize: '18px', textTransform: 'capitalize' } }, theme.name),
                                activeThemeId === theme.id && React.createElement('span', { style: { fontSize: '12px', background: '#22c55e', color: '#fff', padding: '2px 8px', borderRadius: '12px' } }, "ACTIVE")
                            ),
                            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '15px' } },
                                // Toggle for This Theme
                                React.createElement('button', {
                                    onClick: (e) => { e.stopPropagation(); handleToggleActive(theme.id); },
                                    style: { background: 'none', border: 'none', cursor: 'pointer', color: activeThemeId === theme.id ? '#22c55e' : '#666' }
                                }, activeThemeId === theme.id ? React.createElement(ToggleRight, { size: 32 }) : React.createElement(ToggleLeft, { size: 32 })),
                                expandedTheme === theme.id ? React.createElement(ChevronUp, { size: 20 }) : React.createElement(ChevronDown, { size: 20 }),
                                React.createElement('button', {
                                    onClick: async (e) => { e.stopPropagation(); if (confirm('Delete theme?')) await deleteTheme(theme.id); },
                                    className: 'btn-ghost', style: { color: '#ef4444' }
                                }, React.createElement(Trash2, { size: 16 }))
                            )
                        ),
                        expandedTheme === theme.id && React.createElement('div', { style: { padding: '20px' } },
                            React.createElement('div', { style: { marginBottom: '20px', opacity: 0.7, fontSize: '13px' } },
                                "ℹ️ Click any slot to upload a variant. Slots without variants will use the Default icon (faded)."
                            ),
                            ['global', 'subscriber', 'super_fan', 'mega_fan'].map(tier =>
                                React.createElement('div', { key: tier, style: { marginBottom: '30px' } },
                                    React.createElement('h4', { style: { marginBottom: '10px', textTransform: 'uppercase', opacity: 0.7, fontSize: '12px' } },
                                        tier === 'subscriber' ? 'Supporter' : (tier === 'super_fan' ? 'Super Fan' : (tier === 'mega_fan' ? 'Mega Fan' : 'Global'))
                                    ),
                                    React.createElement(RenderTierGrid, { tier, theme })
                                )
                            )
                        )
                    )
                )
            ),

            // RIGHT COLUMN: Sticky Tools
            React.createElement('div', { style: { width: '350px', position: 'sticky', top: '20px', display: 'flex', flexDirection: 'column', gap: '20px' } },

                // TOOL 1: Create New Theme
                React.createElement('div', { className: 'glass-panel', style: { padding: '20px' } },
                    React.createElement('h3', { style: { fontSize: '16px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' } },
                        React.createElement(Sparkles, { size: 18, color: 'var(--primary)' }), "Theme Manager"
                    ),
                    !showAddTheme ? (
                        React.createElement('button', {
                            onClick: () => setShowAddTheme(true),
                            className: 'btn',
                            style: {
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '8px',
                                background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                                border: 'none',
                                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
                                color: 'white',
                                fontWeight: 'bold',
                                padding: '12px',
                                borderRadius: '12px'
                            }
                        }, React.createElement(Plus, { size: 18 }), "Create New Theme")
                    ) : (
                        React.createElement('form', { onSubmit: handleCreateTheme, style: { display: 'flex', flexDirection: 'column', gap: '10px' } },
                            React.createElement('input', {
                                autoFocus: true,
                                value: newThemeName,
                                onChange: e => setNewThemeName(e.target.value.toLowerCase().replace(/\s/g, '')),
                                placeholder: "Theme ID (e.g. christmas)",
                                className: "input-field",
                                style: { width: '100%' }
                            }),
                            React.createElement('div', { style: { display: 'flex', gap: '10px' } },
                                React.createElement('button', { type: 'submit', className: "btn-primary", style: { flex: 1 } }, "Create"),
                                React.createElement('button', { type: 'button', onClick: () => setShowAddTheme(false), className: "btn-ghost", style: { flex: 1 } }, "Cancel")
                            )
                        )
                    )
                ),

                // TOOL 2: Add Base Emote
                React.createElement('div', { className: 'glass-panel', style: { padding: '20px', border: '1px solid var(--border)' } },
                    React.createElement('h3', { style: { fontSize: '16px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' } },
                        React.createElement(Plus, { size: 18, color: '#22c55e' }), "Add Base Emote"
                    ),
                    React.createElement('form', { onSubmit: handleAddEmote, style: { display: 'flex', flexDirection: 'column', gap: '12px' } },
                        React.createElement('div', {},
                            React.createElement('label', { style: { fontSize: '12px', color: '#999', marginBottom: '4px', display: 'block' } }, "Emote Code"),
                            React.createElement('input', {
                                value: newEmote.code,
                                onChange: e => {
                                    let val = e.target.value.replace(/:/g, '');
                                    if (!val) {
                                        setNewEmote({ ...newEmote, code: '', name: '' });
                                    } else {
                                        const codeFinal = `:${val}:`;
                                        const nameAuto = val.replace(/([A-Z])/g, ' $1').trim();
                                        setNewEmote({ ...newEmote, code: codeFinal, name: nameAuto });
                                    }
                                },
                                placeholder: "e.g. MuseSad",
                                className: 'input-field',
                                style: { width: '100%' }
                            })
                        ),
                        React.createElement('div', { style: { display: 'flex', gap: '10px' } },
                            React.createElement('div', { style: { flex: 1 } },
                                React.createElement('label', { style: { fontSize: '12px', color: '#999', marginBottom: '4px', display: 'block' } }, "Display Name"),
                                React.createElement('input', {
                                    value: newEmote.name,
                                    onChange: e => setNewEmote({ ...newEmote, name: e.target.value }),
                                    placeholder: "Name",
                                    className: 'input-field',
                                    style: { width: '100%' }
                                })
                            )
                        ),
                        React.createElement('div', {},
                            React.createElement('label', { style: { fontSize: '12px', color: '#999', marginBottom: '4px', display: 'block' } }, "Image Source"),
                            React.createElement('div', { style: { display: 'flex', gap: '8px' } },
                                React.createElement('input', {
                                    value: newEmote.url,
                                    onChange: e => setNewEmote({ ...newEmote, url: e.target.value }),
                                    placeholder: "Valid Image URL",
                                    className: 'input-field',
                                    style: { flex: 1 }
                                }),
                                React.createElement('div', { style: { position: 'relative' } },
                                    React.createElement('input', {
                                        type: 'file',
                                        accept: 'image/png',
                                        id: 'emote-upload',
                                        style: { display: 'none' },
                                        onChange: handleFileSelect
                                    }),
                                    React.createElement('button', {
                                        type: 'button',
                                        onClick: () => {
                                            if (!newEmote.code) { alert("Enter Code first!"); return; }
                                            document.getElementById('emote-upload').click();
                                        },
                                        className: 'btn-secondary',
                                        style: { height: '100%', padding: '0 12px', borderRadius: '12px' },
                                        title: 'Upload Image'
                                    }, React.createElement(Upload, { size: 16 }))
                                )
                            )
                        ),
                        React.createElement('div', {},
                            React.createElement('label', { style: { fontSize: '12px', color: '#999', marginBottom: '4px', display: 'block' } }, "Tier Tier"),
                            React.createElement('select', {
                                value: newEmote.tier,
                                onChange: e => setNewEmote({ ...newEmote, tier: e.target.value }),
                                className: 'input-field',
                                style: { width: '100%' }
                            },
                                React.createElement('option', { value: 'global' }, 'Global'),
                                React.createElement('option', { value: 'subscriber' }, 'Supporter'),
                                React.createElement('option', { value: 'super_fan' }, 'Super Fan'),
                                React.createElement('option', { value: 'mega_fan' }, 'Mega Fan')
                            )
                        ),
                        React.createElement('button', {
                            type: 'submit',
                            className: 'btn-secondary', // Changed to secondary for grayish look
                            style: { marginTop: '10px', width: '100%', justifyContent: 'center', borderRadius: '12px' }
                        }, React.createElement(Plus, { size: 16 }), " Add Emote Slot")
                    )
                )
            )
        ),
        React.createElement(RenderOverrideModal, null)
    );
}
